import type { Draft, PayloadAction } from '@apia/store';
import type { TRequireOnlyOne } from '@apia/util';
import { handleKey } from './handleKey';
import { setNewFocused } from './setNewFocused';
import { THandleKey, INewFocus, ISelectableState } from './types';

type TState = Draft<Record<string, ISelectableState>>;

export type TPayloadWithId<
  P = Record<string, unknown>,
  T extends string = string,
  M = never,
  E = never,
> = PayloadAction<P & { id: string }, T, M, E>;

export type THandleKeyPayload = TPayloadWithId<THandleKey>;
export type TSelectionStateUpdatePayload = TPayloadWithId<
  Partial<ISelectableState>
>;
export type TSetNewFocusedPayload = TPayloadWithId<INewFocus>;
export type TUpdateByClickPayload = TPayloadWithId<
  TRequireOnlyOne<{ ariaRowIndex: number; rowIndex: number }> &
    TRequireOnlyOne<{ ariaColIndex?: number; colIndex: number }> &
    Pick<INewFocus, 'altKey' | 'ctrlKey' | 'shiftKey' | 'isFocused'>
>;

export function getReduxActions() {
  return {
    handleKey(state: TState, { payload }: THandleKeyPayload) {
      handleKey(state[payload.id], payload);
    },
    selectionStateUpdate(
      state: TState,
      { payload: { id, ...payload } }: TSelectionStateUpdatePayload,
    ) {
      state[id] = { ...state[id], ...payload };
    },
    setNewFocused(
      state: TState,
      { payload: { id, ...payload } }: TSetNewFocusedPayload,
    ) {
      setNewFocused(state[id], payload);
    },
    updateByClick(
      state: TState,
      {
        payload: {
          id,
          colIndex,
          rowIndex,
          ariaColIndex,
          ariaRowIndex,
          isFocused,
          ...payload
        },
      }: TUpdateByClickPayload,
    ) {
      const hasFiltersRow = state[id].hasNonAdditionalFilters;
      state[id].isEditionMode = false;

      const newFocusedColumn = colIndex ?? ariaColIndex ?? 1;
      const newFocusedRow =
        ariaRowIndex !== undefined
          ? ariaRowIndex
          : (rowIndex ?? 0) - (1 + (hasFiltersRow ? 2 : 1));

      setNewFocused(state[id], {
        ...payload,
        newFocusedColumn,
        newFocusedRow,
      });

      if (isFocused !== undefined) state[id].isFocused = isFocused;
    },
  };
}
